import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trophy, Flame, Target, Sprout, Sparkles, ArrowRight, Lock, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleBeatStreakLogin = () => {
  const redirectUrl = window.location.origin + "/streak";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

/**
 * WallOfWins — PUBLIC page at /wall. No auth required.
 *
 * Renders anonymised achievements (handle + streak + goals + total saved)
 * for every user who has explicitly opted in via Settings → "Wall of Wins".
 *
 * Nothing here is auto-shared — the endpoint returns zero rows until at
 * least one user toggles the switch.
 */
export default function WallOfWins() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/wall");
        setData(data);
      } catch (e) {
        console.error("Wall load failed:", e);
        setError(true);
      }
    })();
  }, []);

  const wins = data?.wins || [];

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[var(--bg-primary)]/85 border-b border-[var(--border-default)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="wall-home-link">
            <div className="h-8 w-8 rounded-lg bg-[var(--accent-primary)] flex items-center justify-center logo-breathe">
              <Sprout className="text-white" size={17} strokeWidth={2} />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[var(--text-secondary)] uppercase">Personal</div>
              <div className="font-medium tracking-tight leading-none" style={{fontFamily:'var(--font-heading)'}}>Finance Optimizer</div>
            </div>
          </Link>
          <Link to="/" className="btn-ghost text-sm" data-testid="wall-back-btn">{t("pages.wall.back_home")} <ArrowRight size={14} /></Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div className="label-eyebrow mb-3" style={{ color: "#A16A25" }}>{t("pages.wall.eyebrow")}</div>
        <h1
          className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05]"
          style={{ fontFamily: "var(--font-heading)" }}
          data-testid="wall-title"
        >
          {t("pages.wall.hero_title")}
        </h1>
        <p className="mt-5 text-base md:text-lg text-[var(--text-secondary)] max-w-2xl leading-relaxed">
          {t("pages.wall.hero_body")}
        </p>

        <div className="mt-6 flex items-center gap-2 text-xs text-[var(--text-muted)]">
          <Lock size={12} />
          <span>{t("pages.wall.hero_note")}</span>
        </div>
      </section>

      {/* Wall grid */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        {error ? (
          <div className="text-sm text-[var(--text-muted)]" data-testid="wall-error">
            {t("pages.wall.error_body")}
          </div>
        ) : data === null ? (
          <div className="text-sm text-[var(--text-muted)]" data-testid="wall-loading">
            {t("pages.wall.loading_wins")}
          </div>
        ) : wins.length === 0 ? (
          <div
            className="p-8 rounded-2xl border border-dashed border-[var(--border-default)] bg-[var(--bg-accent)] text-center"
            data-testid="wall-empty"
          >
            <div className="mx-auto h-12 w-12 rounded-full bg-[var(--bg-card)] flex items-center justify-center mb-3 text-[var(--accent-primary)]">
              <Sparkles size={18} />
            </div>
            <div className="font-medium text-lg" style={{fontFamily:'var(--font-heading)'}}>
              {t("pages.wall.empty_wall_title")}
            </div>
            <div className="mt-2 text-sm text-[var(--text-secondary)] max-w-lg mx-auto">
              {t("pages.wall.empty_wall_body")}
            </div>
            <Link to="/" className="btn-primary mt-6 mx-auto" data-testid="wall-cta-signin">
              {t("pages.wall.signin_join")} <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <>
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-muted)] mb-4">
              {wins.length === 1
                ? t("pages.wall.people_sharing_one")
                : t("pages.wall.people_sharing_other", { count: wins.length })}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {wins.map((w, i) => (
                <WinCard key={`${w.handle}-${i}`} rank={i + 1} win={w} signedIn={!!user} t={t} />
              ))}
            </div>
          </>
        )}
      </section>

      <footer className="border-t border-[var(--border-default)] py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 group"
            data-testid="wall-footer-brand"
          >
            <div className="h-8 w-8 rounded-lg bg-[var(--accent-primary)] flex items-center justify-center logo-breathe">
              <Sprout className="text-white" size={16} strokeWidth={2} />
            </div>
            <div className="leading-tight">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)]">
                {t("pages.wall.powered_by")}
              </div>
              <div
                className="text-sm font-medium tracking-tight text-[var(--text-primary)] group-hover:text-[var(--accent-primary)] transition-colors"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Sprout · Personal Finance Optimizer
              </div>
            </div>
          </Link>
          <div className="text-xs text-[var(--text-muted)]">
            {t("pages.wall.footer_note", { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>
    </div>
  );
}

function WinCard({ rank, win, signedIn, t }) {
  const isTop = rank <= 3;
  // Numeric threshold used in the CTA copy — "Beat 12mo" reads better
  // than "Beat this" when there's a real streak to aim at.
  const streakLabel = win.longest_streak > 0
    ? t("pages.wall.beat_streak_mo", { count: win.longest_streak })
    : t("pages.wall.beat_streak_this");
  return (
    <div
      className="card-flat p-5 shadow-soft tilt relative overflow-hidden"
      style={{
        borderColor: isTop ? "rgba(224,158,80,0.35)" : "var(--border-default)",
        background: isTop
          ? "linear-gradient(135deg, rgba(224,158,80,0.10) 0%, rgba(45,90,64,0.06) 100%)"
          : "var(--bg-card)",
      }}
      data-testid={`wall-win-${rank}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center text-sm font-mono font-semibold flex-shrink-0"
            style={{
              background: isTop ? "var(--accent-primary)" : "var(--bg-accent)",
              color: isTop ? "#FDFCFB" : "var(--text-primary)",
            }}
          >
            {rank}
          </div>
          <div className="min-w-0">
            <div className="text-base font-medium leading-tight truncate">{win.handle}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] mt-0.5">
              {t("pages.wall.since")} {win.joined || "—"}
            </div>
          </div>
        </div>
        {isTop && <Trophy size={16} style={{ color: "#E09E50" }} />}
      </div>

      <div className="grid grid-cols-3 gap-2 mt-5">
        <Stat icon={<Flame size={14} />} label={t("pages.wall.stat_streak")} value={t("pages.wall.stat_streak_val", { count: win.longest_streak })} tint="#A16A25" />
        <Stat icon={<Target size={14} />} label={t("pages.wall.stat_goals")} value={String(win.goals_completed)} tint="#2D5A40" />
        <Stat icon={<Sprout size={14} />} label={t("pages.wall.stat_saved")} value={formatMoney(win.total_saved || 0, win.currency)} tint="#437A55" mono />
      </div>

      {isTop && (
        signedIn ? (
          <Link
            to="/streak"
            data-testid={`wall-cta-${rank}`}
            className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--accent-primary)] hover:underline"
          >
            <Zap size={13} /> {t("pages.wall.see_my_streak")}
          </Link>
        ) : (
          <button
            type="button"
            onClick={handleBeatStreakLogin}
            data-testid={`wall-cta-${rank}`}
            className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--accent-primary)] hover:underline"
            title={t("pages.wall.beat_tooltip")}
          >
            <Zap size={13} /> {t("pages.wall.beat_streak", { label: streakLabel })} <ArrowRight size={12} />
          </button>
        )
      )}
    </div>
  );
}

function Stat({ icon, label, value, tint, mono }) {
  return (
    <div className="rounded-md p-2 bg-[var(--bg-accent)]">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em]" style={{ color: tint }}>
        {icon}<span>{label}</span>
      </div>
      <div className={`mt-1 leading-none ${mono ? "font-mono text-[13px]" : "text-sm"} font-semibold text-[var(--text-primary)] truncate`}>
        {value}
      </div>
    </div>
  );
}
